import { useState, useEffect } from 'react';
import { AlertTriangle, Infinity, CheckCircle, XCircle, RefreshCw } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { apiClient, CapacityCheck } from '../../services/api';

interface DispatchLimitIndicatorProps {
  wabaAccountId: string;
  recipientCount: number;
  onCapacityCheck?: (result: CapacityCheck) => void;
  // Optional: allow passing capacity info and loading state from parent
  isLoading?: boolean;
  capacityInfo?: CapacityCheck | null;
}

export default function DispatchLimitIndicator({
  wabaAccountId,
  recipientCount,
  onCapacityCheck,
  isLoading: externalLoading,
  capacityInfo: externalCapacity,
}: DispatchLimitIndicatorProps) {
  const { accessToken } = useAuth();
  const [internalLoading, setInternalLoading] = useState(false);
  const [internalCapacity, setInternalCapacity] = useState<CapacityCheck | null>(null);
  const [error, setError] = useState('');

  // Use external values if provided, otherwise use internal state
  const loading = externalLoading !== undefined ? externalLoading : internalLoading;
  const capacity = externalCapacity !== undefined ? externalCapacity : internalCapacity;

  useEffect(() => {
    // Only auto-fetch if external capacity is not provided
    if (externalCapacity === undefined && wabaAccountId && recipientCount > 0) {
      checkCapacity();
    } else if (externalCapacity === undefined) {
      setInternalCapacity(null);
    }
  }, [wabaAccountId, recipientCount, accessToken, externalCapacity]);

  const checkCapacity = async () => {
    if (!accessToken || !wabaAccountId || recipientCount <= 0) return;

    try {
      setInternalLoading(true);
      setError('');

      const response = await apiClient.checkDispatchCapacity(
        wabaAccountId,
        recipientCount,
        accessToken
      );

      if (response.success) {
        setInternalCapacity(response.data);
        onCapacityCheck?.(response.data);
      }
    } catch (err: any) {
      setError(err.message || 'Erro ao verificar capacidade');
      setInternalCapacity(null);
    } finally {
      setInternalLoading(false);
    }
  };

  if (!wabaAccountId) {
    return null;
  }

  if (loading) {
    return (
      <div className="flex items-center gap-2 p-3 bg-bg-1 border border-bg-3 rounded-lg">
        <RefreshCw size={16} className="animate-spin text-accent-1" />
        <span className="text-sm text-text-2">Verificando limite...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 p-3 bg-error/10 border border-error/30 rounded-lg">
        <AlertTriangle size={16} className="text-error" />
        <span className="text-sm text-error">{error}</span>
      </div>
    );
  }

  if (!capacity) {
    return null;
  }

  // Calculate status
  const canDispatch = capacity.canDispatch;
  const isUnlimited = capacity.isUnlimited;
  const usagePercent = isUnlimited ? 0 : (capacity.used / capacity.limit) * 100;
  const remaining = capacity.available;

  if (isUnlimited) {
    return (
      <div className="flex items-center gap-2 p-3 bg-success/10 border border-success/30 rounded-lg">
        <Infinity size={16} className="text-success" />
        <div className="flex-1">
          <p className="text-sm font-medium text-success">Limite ilimitado</p>
          <p className="text-xs text-success/80">
            Voce pode disparar para {recipientCount.toLocaleString()} destinatarios
          </p>
        </div>
        <CheckCircle size={18} className="text-success" />
      </div>
    );
  }

  if (!canDispatch) {
    return (
      <div className="flex items-center gap-2 p-3 bg-error/10 border border-error/30 rounded-lg">
        <XCircle size={16} className="text-error" />
        <div className="flex-1">
          <p className="text-sm font-medium text-error">Limite excedido</p>
          <p className="text-xs text-error/80">
            Voce precisa de {recipientCount.toLocaleString()} mas so tem{' '}
            {remaining.toLocaleString()} disponiveis ({capacity.used.toLocaleString()}/
            {capacity.limit.toLocaleString()} usado)
          </p>
        </div>
      </div>
    );
  }

  // Can dispatch
  const isWarning = usagePercent > 70 || remaining - recipientCount < capacity.limit * 0.1;

  return (
    <div
      className={`p-3 rounded-lg border ${
        isWarning
          ? 'bg-warning/10 border-warning/30'
          : 'bg-success/10 border-success/30'
      }`}
    >
      <div className="flex items-center gap-2 mb-2">
        {isWarning ? (
          <AlertTriangle size={16} className="text-warning" />
        ) : (
          <CheckCircle size={16} className="text-success" />
        )}
        <p className={`text-sm font-medium ${isWarning ? 'text-warning' : 'text-success'}`}>
          {isWarning ? 'Proximo do limite' : 'Disparo permitido'}
        </p>
      </div>

      <div className="space-y-2">
        <div className="flex justify-between text-xs">
          <span className="text-text-3">Limite diario</span>
          <span className="text-text-2">{capacity.limit.toLocaleString()}</span>
        </div>

        <div className="flex justify-between text-xs">
          <span className="text-text-3">Usado (24h)</span>
          <span className="text-text-2">{capacity.used.toLocaleString()}</span>
        </div>

        <div className="flex justify-between text-xs">
          <span className="text-text-3">Disponivel</span>
          <span className={`font-medium ${isWarning ? 'text-warning' : 'text-success'}`}>
            {remaining.toLocaleString()}
          </span>
        </div>

        <div className="flex justify-between text-xs">
          <span className="text-text-3">Este disparo</span>
          <span className="text-text-2">{recipientCount.toLocaleString()} destinatarios</span>
        </div>

        <div className="flex justify-between text-xs">
          <span className="text-text-3">Apos disparo</span>
          <span
            className={`font-medium ${
              remaining - recipientCount < capacity.limit * 0.1 ? 'text-warning' : 'text-text-1'
            }`}
          >
            {(remaining - recipientCount).toLocaleString()} restantes
          </span>
        </div>

        {/* Progress bar */}
        <div className="mt-2">
          <div className="h-2 bg-bg-3 rounded-full overflow-hidden">
            <div
              className={`h-full transition-all ${
                usagePercent > 90
                  ? 'bg-error'
                  : usagePercent > 70
                  ? 'bg-warning'
                  : 'bg-success'
              }`}
              style={{ width: `${Math.min(100, usagePercent)}%` }}
            />
          </div>
          <p className="text-xs text-text-3 mt-1 text-right">
            {usagePercent.toFixed(0)}% utilizado
          </p>
        </div>
      </div>
    </div>
  );
}
